import { createElement } from 'react'
import type { ModalFuncProps } from 'antd'
import type { TFunction } from 'i18next'
import { brandModalConfirmProps } from '@/components/BrandButton'
import {
  cancelSchedule as cancelScheduleApi,
  deleteVersion as deleteVersionApi,
  delistVersion as delistVersionApi,
  disableVersion as disableVersionApi,
  enableVersion as enableVersionApi,
  releaseNow as releaseNowApi,
  relistVersion as relistVersionApi,
  rollbackVersion as rollbackVersionApi,
  withdrawReview as withdrawReviewApi,
} from '@/api/versions'
import type { AppVersion, VersionStatus } from '@/types'
import { hasEverReleased } from './platform'
import { refreshAppVersions } from './refreshAppVersions'

export type VersionListAction =
  | 'delete'
  | 'withdraw'
  | 'cancelSchedule'
  | 'releaseNow'
  | 'delist'
  | 'disable'
  | 'relist'
  | 'enable'
  | 'rollback'

/** 列表行 hover 操作（按状态）。回滚入口已隐藏，已下线且曾上线的版本不提供操作。 */
export function listActionsForStatus(
  status: VersionStatus,
  opts?: {
    /** 已下线但从未上线（取消定时 / 到期失败）：走立即上线 */
    neverReleased?: boolean
  },
): VersionListAction[] {
  switch (status) {
    case 'developing':
    case 'rejected':
      return ['delete']
    case 'reviewing':
      return ['withdraw']
    case 'scheduled':
      return ['cancelSchedule', 'releaseNow']
    case 'released':
      return ['delist', 'disable']
    case 'delisted':
      return ['relist', 'disable']
    case 'disabled':
      return ['enable']
    case 'archived':
      if (opts?.neverReleased) return ['releaseNow']
      return []
    case 'rolling_back':
      return []
    default:
      return []
  }
}

/**
 * 是否允许对「已下线」版本展示回滚：
 * 须曾上线过，且同应用当前有唯一线上版本可被整版替换。
 */
export function canRollbackArchivedVersion(
  version: Pick<AppVersion, 'id' | 'status' | 'released_at'>,
  appVersions: Pick<AppVersion, 'id' | 'status'>[],
): boolean {
  if (version.status !== 'archived' || !hasEverReleased(version)) return false
  return !!findOnlineVersion(appVersions, version.id)
}

/** 当前唯一线上版本（排除自身）；没有则 undefined */
export function findOnlineVersion<T extends Pick<AppVersion, 'id' | 'status'>>(
  versions: T[],
  excludeId?: string,
): T | undefined {
  return versions.find((v) => v.status === 'released' && v.id !== excludeId)
}

/** 会让目标成为唯一线上版本、顶替当前线上版本的操作 */
const REPLACES_ONLINE = new Set<VersionListAction>([
  'releaseNow',
  'relist',
  'enable',
  'rollback',
])

/** 侧栏 … 菜单（仅上架相关，Owner） */
export function siderMenuActionsForStatus(
  status: VersionStatus,
): VersionListAction[] {
  switch (status) {
    case 'released':
      return ['delist', 'disable']
    case 'delisted':
      return ['relist', 'disable']
    case 'disabled':
      return ['enable']
    default:
      return []
  }
}

export function isDangerAction(action: VersionListAction): boolean {
  return (
    action === 'delete' ||
    action === 'delist' ||
    action === 'disable' ||
    action === 'rollback'
  )
}

export function actionLabelKey(action: VersionListAction): string {
  return `versions.action_${action}`
}

type StoreActions = {
  deleteVersion: (app_id: string, version_id: string) => void
  withdrawVersion: (app_id: string, version_id: string) => void
  cancelSchedule: (app_id: string, version_id: string) => void
  releaseNow: (app_id: string, version_id: string) => void
  delistVersion: (app_id: string, version_id: string) => void
  disableVersion: (app_id: string, version_id: string) => void
  enableVersion: (app_id: string, version_id: string) => void
  relistVersion: (app_id: string, version_id: string) => void
  rollbackVersion: (app_id: string, version_id: string) => void
}

/** 先打真实接口，成功后再改本地 store */
async function callVersionActionApi(
  action: VersionListAction,
  app_id: string,
  version_id: string,
): Promise<void> {
  switch (action) {
    case 'delete':
      await deleteVersionApi(app_id, version_id)
      break
    case 'withdraw':
      await withdrawReviewApi(app_id, version_id)
      break
    case 'cancelSchedule':
      await cancelScheduleApi(app_id, version_id)
      break
    case 'releaseNow':
      await releaseNowApi(app_id, version_id)
      break
    case 'delist':
      await delistVersionApi(app_id, version_id)
      break
    case 'disable':
      await disableVersionApi(app_id, version_id)
      break
    case 'enable':
      await enableVersionApi(app_id, version_id)
      break
    case 'relist':
      await relistVersionApi(app_id, version_id)
      break
    case 'rollback':
      await rollbackVersionApi(app_id, version_id)
      break
  }
}

function applyLocalAction(
  store: StoreActions,
  action: VersionListAction,
  app_id: string,
  version_id: string,
) {
  switch (action) {
    case 'delete':
      store.deleteVersion(app_id, version_id)
      break
    case 'withdraw':
      store.withdrawVersion(app_id, version_id)
      break
    case 'cancelSchedule':
      store.cancelSchedule(app_id, version_id)
      break
    case 'releaseNow':
      store.releaseNow(app_id, version_id)
      break
    case 'delist':
      store.delistVersion(app_id, version_id)
      break
    case 'disable':
      store.disableVersion(app_id, version_id)
      break
    case 'enable':
      store.enableVersion(app_id, version_id)
      break
    case 'relist':
      store.relistVersion(app_id, version_id)
      break
    case 'rollback':
      store.rollbackVersion(app_id, version_id)
      break
  }
}

type ConfirmModal = {
  confirm: (props: ModalFuncProps) => void
}

type ActionToast = {
  success: (content: string) => void
  error: (content: string) => void
}

/** 会顶替当前线上版本时，返回「当前线上版本 vX 将下线」 */
export function replacesOnlineLine(
  t: TFunction,
  action: VersionListAction,
  version: Pick<AppVersion, 'id'>,
  appVersions: Pick<AppVersion, 'id' | 'status' | 'version'>[],
): string | undefined {
  if (!REPLACES_ONLINE.has(action)) return undefined
  const online = findOnlineVersion(appVersions, version.id)
  return online
    ? t('versions.replacesOnline', { version: online.version })
    : undefined
}

/** Confirm 后：真实接口 → 本地状态机。必须用 App.useApp() 的 modal，静态 Modal.confirm 拿不到主题色。
 * 会顶替线上版本的操作：不乐观改行，等整表刷新回来再关弹窗。
 */
export function confirmVersionAction(opts: {
  t: TFunction
  modal: ConfirmModal
  message: ActionToast
  action: VersionListAction
  version: AppVersion
  app_id: string
  store: StoreActions
  /** 同应用全部版本（含自身）；用于提示会被顶替的当前线上版本 */
  appVersions?: Pick<AppVersion, 'id' | 'status' | 'version'>[]
  onDone?: () => void
}) {
  const { t, modal, message, action, version, app_id, store, appVersions, onDone } =
    opts
  const v = version.version

  const title = t(`versions.confirm_${action}_title`, { version: v })
  const body = t(`versions.confirm_${action}_body`, { version: v })
  const extra = replacesOnlineLine(t, action, version, appVersions ?? [])
  const content = extra
    ? createElement(
        'div',
        null,
        createElement('div', null, body),
        createElement('div', { style: { marginTop: 8 } }, extra),
      )
    : body
  const okText = t(`versions.confirm_${action}_ok`)

  modal.confirm({
    title,
    content,
    okText,
    cancelText: t('common.cancel'),
    ...brandModalConfirmProps(
      isDangerAction(action) ? 'danger' : 'primary',
    ),
    onOk: async () => {
      try {
        await callVersionActionApi(action, app_id, version.id)
        if (REPLACES_ONLINE.has(action)) {
          await refreshAppVersions(app_id)
        } else {
          applyLocalAction(store, action, app_id, version.id)
        }
        message.success(t('versions.actionSuccess'))
        onDone?.()
      } catch (err) {
        message.error(err instanceof Error ? err.message : 'Request failed')
        throw err
      }
    },
  })
}
